import { EditableComponent, EditableComponentProps } from '@adobe/aem-react-editable-components';
import React, { FC, useMemo, useState, useCallback, useEffect, useRef, startTransition } from 'react';
import { useUserDetailsStore } from '@marriott/mi-store-utils';
import clsx from 'clsx';
import { renditions } from '@marriott/mi-ui-library';
import {
  DcaNode,
  DynamicTabbedLayoutCompProps,
  DynamicTabbedLayoutProps,
  SlotContent,
  AlloyResult,
  AlloyProposition,
} from './DynamicTabbedLayout.types';
import { TabNavigation } from '../../molecules/Tabs/TabNavigation';
import { TabBanner } from '../../molecules/Tabs/TabBanner';
import { StyledDynamicTabbedLayout, StyledTabPanelGrid, StyledTabPanelItem } from './DynamicTabbedLayout.styles';
import { fetchCFData } from '../../utils/fetchCFData';
import {
  buildPatternSlots,
  overlaySlotsWithDcaRes,
  buildLocalDcaNodes,
  mergeCfAndDcaToSlots,
} from '../../utils/dynamicContentUtils';
import { resolveSlotComponent } from '../../utils/resolveSlotComponent';

const TabContainerConfig = {
  emptyLabel: 'Dynamic Tabbed Layout',
  isEmpty: () => true,
  resourceType: `mi-aem-homepage-spa/components/content/dynamictabbedlayout`,
};

export const DynamicTabbedLayout = (props: EditableComponentProps & DynamicTabbedLayoutProps) => (
  <EditableComponent config={{ ...TabContainerConfig }} {...props}>
    <DynamicTabbedLayoutComp {...props.model} isAuthorMode={props.isAuthorMode} />
  </EditableComponent>
);

const DynamicTabbedLayoutComp: FC<DynamicTabbedLayoutCompProps> = (model: DynamicTabbedLayoutCompProps) => {
  const {
    styleclass,
    targetComponentName,
    trackingProperties,
    tabDetails = [],
    pageTitle,
    requestedTab,
    requestedTabData,
    isAuthorMode = false,
  } = model;

  const userState = useUserDetailsStore(state => state.isSignedInUser);
  const theme = styleclass?.includes('inverse') ? 'dark' : 'light';

  // build tab list (tabTitle is user-facing and used for hash / API)
  const tabItemList = useMemo(
    () =>
      tabDetails.map((tab, index) => ({
        tabValue: tab.nodeName,
        tabTitle: tab.tabName,
        tabIcon: tab.tabIcon?.split(':')[1] ?? '',
        tabTrackingDescription: tab.tabTrackingDescription,
        dynamicMedia: tab.dynamicMedia,
        customTabClickTrackValue: `${trackingProperties?.trackingContentPosition}|Tab${index + 1}-${
          tab.tabName
        }|internal`,
        tabDCAtiles: tab.dcaTilesList ?? [],
        tabPatternSlots: buildPatternSlots(tab.bannerFirst ?? false, tab.squareFirst ?? true, tab.dcaTilesList ?? []),
        tabDecisionScopes: tab.dcaTilesList?.map((dt: DcaNode) => dt.mboxParameter).filter(Boolean) ?? ([] as string[]),
      })),
    [tabDetails, trackingProperties?.trackingContentPosition]
  );

  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(requestedTab ?? tabItemList[0]?.tabTitle);
  const [tabSlots, setTabSlots] = useState<Record<string, SlotContent[]>>(() => {
    if (requestedTab && Array.isArray(requestedTabData)) {
      return { [requestedTab]: requestedTabData };
    }
    return {};
  });
  const tabSlotsRef = useRef<Record<string, SlotContent[]>>(
    requestedTab && Array.isArray(requestedTabData) ? { [requestedTab]: requestedTabData } : {}
  );
  useEffect(() => {
    tabSlotsRef.current = tabSlots;
  }, [tabSlots]);

  async function getCFDataFromApi(tabTitle: string) {
    try {
      const res = await fetch(`/api/getCFData?tabName=${encodeURIComponent(tabTitle)}`);
      if (!res.ok) return [];
      const json = await res.json();
      return json.cf ?? [];
    } catch (e) {
      console.error('Error calling getCFData API:', e);
      return [];
    }
  }
  // fetch: server provides CF-filled slots + decisionScopes + slotsMeta
  const inFlightRef = useRef<Set<string>>(new Set());
  const fetchTabData = useCallback(
    async (tabTitle: string) => {
      if (tabSlotsRef.current[tabTitle]) return; // already loaded (e.g., SSR-provided)
      if (inFlightRef.current.has(tabTitle)) return;
      inFlightRef.current.add(tabTitle);

      try {
        const tabMeta = tabItemList.find(t => t.tabTitle === tabTitle);
        if (!tabMeta) return;
        const tabDecisionScopes = tabMeta.tabDecisionScopes ?? [];
        const contentFragments = isAuthorMode
          ? await fetchCFData(tabTitle, isAuthorMode, userState)
          : await getCFDataFromApi(tabTitle);
        console.log('[DTL] getCFData response:', {
          tabTitle,
          contentFragmentsCount: contentFragments.length,
          decisionScopes: tabDecisionScopes.length,
        });

        const mergedSlots = mergeCfAndDcaToSlots(
          tabMeta.tabPatternSlots || [],
          tabMeta.tabDCAtiles || [],
          contentFragments
        );

        setTabSlots(prev => {
          const next = { ...prev, [tabTitle]: mergedSlots || [] };
          return next;
        });
        // start alloy overlay (async, non-blocking)
        void populateDcaFromAlloy(tabTitle, tabMeta.tabDCAtiles, tabDecisionScopes as string[]);
      } catch (err) {
        console.error('Error fetching dynamic tab data', err);
      } finally {
        inFlightRef.current.delete(tabTitle);
      }
    },
    [tabItemList]
  );

  // DCA overlay (client-side). If NEXT_PUBLIC_USE_MOCK === 'true' use local builder.
  const dcaInFlightRef = useRef<Set<string>>(new Set());
  const populateDcaFromAlloy = useCallback(
    async (tabTitle: string, dcaTiles?: DcaNode[], tabDecisionScopes?: string[]) => {
      // keep a list of added keys locally so we can delete them deterministically
      console.log('[populateDcaFromAlloy] tabTitle', tabTitle);
      const addedKeys: string[] = [];
      try {
        if (!dcaTiles?.length) return;
        let dcaNodes: DcaNode[] = [];

        const useMock = process.env['USE_MOCK_DCADATA'] === 'true';
        if (useMock) {
          dcaNodes = buildLocalDcaNodes(tabTitle, dcaTiles || []);
        } else {
          // production: call alloy
          const w = window as unknown as { alloy?: (command: string, payload: unknown) => Promise<unknown> };
          if (typeof window === 'undefined' || !w.alloy) return;

          const pendingMboxes =
            tabDecisionScopes?.filter((mbox: string) => {
              const key = `${tabTitle}::${mbox ?? ''}`;
              if (dcaInFlightRef.current.has(key)) return false;
              return true;
            }) ?? [];

          if (!pendingMboxes.length) return;

          // mark them in-flight
          pendingMboxes.forEach(m => {
            const key = `${tabTitle}::${m}`;
            dcaInFlightRef.current.add(key);
            addedKeys.push(key);
          });

          const result = (await w.alloy('sendEvent', {
            xdm: {},
            decisionScopes: pendingMboxes,
          })) as AlloyResult;

          // Alloy may return decisions or propositions depending on setup; normalize defensively
          const propositions: AlloyProposition[] = result?.propositions ?? result?.decisions ?? [];
          // Map Alloy results to our DCA nodes, resolving order from authored tiles via mbox
          const orderByMbox = new Map<string, number>();
          (dcaTiles || []).forEach((t: DcaNode) => {
            const mboxParam = t?.mboxParameter;
            const ord = Number(t?.order);
            if (mboxParam && !Number.isNaN(ord)) orderByMbox.set(mboxParam, ord);
          });
          dcaNodes = (propositions || []).map((p: AlloyProposition) => {
            const mbox = p?.scope ?? p?.id ?? undefined;
            // Determine payload defensively
            let payload: unknown = null;
            const items = p?.items;
            if (Array.isArray(items) && items.length > 0) {
              const first = items[0] as Record<string, unknown>;
              const dataVal = first?.['data'] as unknown;
              if (dataVal && typeof dataVal === 'object') {
                const dataObj = dataVal as Record<string, unknown>;
                payload = 'content' in dataObj ? (dataObj['content'] as unknown) : dataVal;
              } else {
                payload = first;
              }
            } else {
              payload = p as unknown;
            }
            const order = mbox ? orderByMbox.get(mbox) : undefined;
            return { mboxParameter: mbox, payload, order } as DcaNode;
          });
        }

        if (!dcaNodes?.length) return;
        // overlay onto existing slots for that tab
        setTabSlots(prev => {
          const slotsForTab = prev[tabTitle];
          if (!slotsForTab) return prev;
          const updated = overlaySlotsWithDcaRes(slotsForTab, dcaNodes);
          console.log(
            '[populateDcaFromAlloy] updated slots:',
            updated.map(s => `${s?.position}-${s?.type}`)
          );
          return { ...prev, [tabTitle]: updated as SlotContent[] };
        });
      } catch (e) {
        // On Alloy failure: fallback to cfFallback for DCA slots
        console.warn('[populateDcaFromAlloy] Alloy call failed, using cfFallback for DCA slots', e);
        setTabSlots(prev => {
          const slotsForTab = prev[tabTitle];
          if (!slotsForTab) return prev;
          const updated = overlaySlotsWithDcaRes(slotsForTab, [], { fillFromCFFallbackIfEmpty: true });
          console.log(
            '[populateDcaFromAlloy] updated slots:',
            updated.map(s => `${s?.position}-${s?.type}`)
          );
          return { ...prev, [tabTitle]: updated as SlotContent[] };
        });
      } finally {
        // cleanup only the keys we added in this invocation
        addedKeys.forEach(k => dcaInFlightRef.current.delete(k));
      }
    },
    [tabSlots]
  );

  useEffect(() => {
    // Mark hydration complete; data from SSR is already present in state
    setIsHydrated(true);
  }, []);

  // If SSR provided requestedTabData, still attempt to overlay with Alloy after hydration
  // This is to restrict it to the SSR tab only, not the client-selected tab.
  useEffect(() => {
    if (!isHydrated) return; // wait for hydration to complete
    if (!requestedTab || !requestedTabData) return; // no requested tab data
    const tabMeta = tabItemList.find(t => t.tabTitle === requestedTab);
    if (!tabMeta) return;
    void populateDcaFromAlloy(requestedTab, tabMeta.tabDCAtiles, tabMeta.tabDecisionScopes as string[]);
  }, [isHydrated, requestedTab, requestedTabData]);

  // Fetch when selectedTab changes (after hydration)
  useEffect(() => {
    if (!isHydrated) return;
    if (!selectedTab) return;
    void fetchTabData(selectedTab);
  }, [isHydrated, selectedTab, fetchTabData]);

  // Sync URL with selectedTab on hydration — handles edge cases where the URL
  // still contains a stale/invalid tab value (e.g. browser restored from bfcache).
  useEffect(() => {
    if (!isHydrated) return;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    // If URL tab doesn't match selected tab, silently correct the URL
    if (tabParam && tabParam !== selectedTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', selectedTab);
      window.history.replaceState(null, '', url.toString());
    }
  }, [isHydrated]); // intentionally run once after hydration

  // sync query param (?tab=) changes via browser navigation -> selectedTab
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const firstTab = tabItemList[0]?.tabTitle;

      // No tab param — default to first tab and correct the URL
      if (!tabParam) {
        if (firstTab) {
          startTransition(() => setSelectedTab(firstTab));
          const url = new URL(window.location.href);
          url.searchParams.set('tab', firstTab);
          window.history.replaceState(null, '', url.toString());
        }
        return;
      }

      // Valid tab — select it (case-insensitive match)
      const matched = tabItemList.find(tab => tab.tabTitle.toLowerCase() === tabParam.toLowerCase());
      if (matched) {
        startTransition(() => setSelectedTab(matched.tabTitle));
      } else if (firstTab) {
        // Invalid tab — default to first tab and correct the URL
        startTransition(() => setSelectedTab(firstTab));
        const url = new URL(window.location.href);
        url.searchParams.set('tab', firstTab);
        window.history.replaceState(null, '', url.toString());
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tabItemList]);

  // click handler: update selectedTab (fetchTabData runs via effect)
  const onTabSelect = useCallback((tabTitle: string) => {
    startTransition(() => setSelectedTab(tabTitle));
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabTitle);
    window.history.pushState(null, '', `${url.pathname}?${url.searchParams.toString()}`);
  }, []);

  const selectedTabMeta = useMemo(() => tabItemList.find(t => t.tabTitle === selectedTab), [tabItemList, selectedTab]);
  const currentSlots = tabSlots[selectedTab] ?? [];
  const panelSlots = useMemo(() => {
    const patternSlots = selectedTabMeta?.tabPatternSlots ?? [];
    if (!patternSlots.length) return currentSlots;
    if (!currentSlots.length) return patternSlots as SlotContent[];

    const slotsByPosition = new Map<number, SlotContent>();
    currentSlots.forEach(slot => {
      if (typeof slot?.position === 'number') slotsByPosition.set(slot.position, slot);
    });

    return patternSlots.map(pattern => {
      const match = slotsByPosition.get(pattern.position);
      if (!match) return pattern as SlotContent;
      // Keep layout from pattern; merge data from loaded slots.
      return { ...match, ...pattern } as SlotContent;
    });
  }, [selectedTabMeta, currentSlots]);
  const selectedTabDynamicMedia = useMemo(
    () => selectedTabMeta?.dynamicMedia ?? (tabItemList[0]?.dynamicMedia as renditions),
    [selectedTabMeta, tabItemList]
  );
  const slotItems = useMemo(() => {
    const rawTabIndex = tabItemList.findIndex(t => t.tabTitle === selectedTab);
    const tabIndex = rawTabIndex >= 0 ? rawTabIndex : 0;
    const cardTrackingPosition = `${trackingProperties?.trackingContentPosition}-Tab${tabIndex + 1}`;
    let cardIndex = 0;

    return (panelSlots || []).map(slot => {
      const key = `slot-${slot?.position ?? 0}`;
      const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
      const isBanner = size === 'banner';
      const currentCardIndex = isBanner ? 0 : cardIndex++;
      const colClass = isBanner ? '' : `pos-${(currentCardIndex % 3) + 1}`;

      return (
        <StyledTabPanelItem key={key} className={clsx(size, colClass)}>
          {resolveSlotComponent(slot, {
            index: currentCardIndex,
            trackingProperties: {
              ...trackingProperties,
              trackingContentPosition: cardTrackingPosition,
            },
          })}
        </StyledTabPanelItem>
      );
    });
  }, [panelSlots, tabItemList, selectedTab, trackingProperties]);

  return (
    <StyledDynamicTabbedLayout
      className={clsx('m-container-fullbleed', styleclass)}
      data-target-component={targetComponentName || null}
      data-testid="homepage-DynamicTabbedLayout"
      data-component-name="o-homepage-DynamicTabbedLayout"
    >
      <TabBanner theme={theme} dynamicMedia={selectedTabDynamicMedia} />

      <TabNavigation
        selectedTab={selectedTab}
        setSelectedTab={onTabSelect}
        tabNavigationList={tabItemList}
        pageTitle={pageTitle}
      >
        <StyledTabPanelGrid>{slotItems}</StyledTabPanelGrid>
      </TabNavigation>
    </StyledDynamicTabbedLayout>
  );
};
